// Lazy route chunks can fail to load for two reasons (#41): the tab outlived a
// deploy and asks for hashes that no longer exist, or the network dropped
// (iOS Safari reports the module, the CSS and Stripe.js failing at once).
// A single reload fixes the first case. If it fails again it is the network,
// so the user gets the ConnectionError view with a retry instead of a frozen screen.

export const CHUNK_RELOAD_KEY = 'owqlo:chunk-reload'

const CHUNK_ERROR_PATTERNS = [
  /Importing a module script failed/i, // Safari
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Unable to preload CSS/i, // Vite preload helper
]

export const isChunkLoadError = (error) => {
  const message = error?.message
  return typeof message === 'string' && CHUNK_ERROR_PATTERNS.some((re) => re.test(message))
}

const hasReloaded = (storage) => {
  try {
    return storage.getItem(CHUNK_RELOAD_KEY) !== null
  } catch {
    return true
  }
}

// Returns false when the flag cannot be persisted: reloading without it could loop forever.
const markReloaded = (storage) => {
  try {
    storage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    return true
  } catch {
    return false
  }
}

const clearReloaded = (storage) => {
  try {
    storage.removeItem(CHUNK_RELOAD_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

const tryReload = (storage) => !hasReloaded(storage) && markReloaded(storage)

const browserNavigate = (url) => {
  if (url) window.location.assign(url)
  else window.location.reload()
}

const safeSessionStorage = () => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

const unavailableStorage = {
  getItem: () => { throw new Error('sessionStorage unavailable') },
  setItem: () => { throw new Error('sessionStorage unavailable') },
  removeItem: () => {},
}

export function installChunkLoadRecovery({
  router,
  target = window,
  storage = safeSessionStorage() ?? unavailableStorage,
  navigate = browserNavigate,
}) {
  // Vite dispatches this for both the CSS preload and the module import itself.
  target.addEventListener('vite:preloadError', (event) => {
    if (!tryReload(storage)) return // let it propagate to router.onError
    event.preventDefault()
    navigate()
  })

  router.onError((error, to) => {
    if (!isChunkLoadError(error)) return
    if (to?.name === 'ConnectionError') return // its own chunk failed: nothing left to show
    if (tryReload(storage)) {
      navigate(router.resolve(to).href)
      return
    }
    router.push({ name: 'ConnectionError', query: { redirect: to?.fullPath } }).catch(() => {})
  })

  router.afterEach((to, from, failure) => {
    if (!failure) clearReloaded(storage)
  })
}
